import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [pronosticsOpen, setPronosticsOpen] = useState(false);
    const [resourcesOpen, setResourcesOpen] = useState(false);

    const [mobilePronosticsOpen, setMobilePronosticsOpen] = useState(false);
    const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);

    const [theme, setTheme] = useState(() => {
        const storedTheme = localStorage.getItem("theme");
        if (storedTheme) return storedTheme;
        return "dark";
    });

    useEffect(() => {
        if (theme === "light") {
            document.documentElement.classList.add("light");
            document.documentElement.classList.remove("dark");
        } else {
            document.documentElement.classList.remove("light");
            document.documentElement.classList.add("dark");
        }
    }, [theme]);

    const toggleTheme = () => {
        const nextTheme = theme === "dark" ? "light" : "dark";
        setTheme(nextTheme);
        localStorage.setItem("theme", nextTheme);
    };

    return (
        <header
            className="glass-header sticky top-0 z-50 border-b border-border"
            data-testid="site-header"
        >
            <div className="container-x flex items-center justify-between h-16 lg:h-18">
                <Link
                    to="/"
                    className="flex items-center gap-2 group"
                    data-testid="logo-home-link"
                >
                    <div className="w-9 h-9 rounded-lg bg-[#00C853] flex items-center justify-center font-heading font-bold text-black text-lg shadow-lg group-hover:rotate-6 transition-transform">
                        PS
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 dark:text-white text-lg">
                            Pronospot
                        </div>
                        <div className="text-[10px] text-[#00C853] uppercase tracking-widest">
                            .com
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {/* 1. Comparatif */}
                    <NavLink
                        to="/comparatif"
                        data-testid="nav-comparatif"
                        className={({ isActive }) =>
                            `text-sm font-medium transition-colors hover:text-[#00C853] ${
                                isActive ? "text-[#00C853]" : "text-slate-700 dark:text-gray-200"
                            }`
                        }
                    >
                        Comparatif
                    </NavLink>

                    {/* 2. Pronostics Dropdown */}
                    <div
                        className="relative py-4"
                        onMouseEnter={() => setPronosticsOpen(true)}
                        onMouseLeave={() => setPronosticsOpen(false)}
                    >
                        <button
                            className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#00C853] ${
                                pronosticsOpen ? "text-[#00C853]" : "text-slate-700 dark:text-gray-200"
                            }`}
                        >
                            Compétitions
                            <ChevronDown
                                size={14}
                                className={`transition-transform duration-200 ${
                                    pronosticsOpen ? "rotate-180 text-[#00C853]" : ""
                                }`}
                            />
                        </button>
                        {pronosticsOpen && (
                            <div className="absolute top-full left-0 mt-0.5 w-64 rounded-xl bg-card border border-border shadow-2xl p-2 animate-fade-in-up duration-200 max-h-[400px] overflow-y-auto">
                                {[
                                    { to: "/coupe-du-monde-2026", label: "Coupe du Monde 2026" },
                                    { to: "/competitions/ligue-1", label: "Ligue 1 France" },
                                    { to: "/competitions/premier-league", label: "Premier League Angleterre" },
                                    { to: "/competitions/la-liga", label: "Liga Primera Espagne" },
                                    { to: "/competitions/serie-a", label: "Serie A Italie" },
                                    { to: "/competitions/bundesliga", label: "Bundesliga Allemagne" },
                                    { to: "/competitions/champions-league", label: "Ligue des Champions" },
                                    { to: "/competitions/europa-league", label: "Ligue Europa" },
                                    { to: "/competitions/primeira-liga", label: "Primeira Liga Portugal" },
                                    { to: "/competitions/pro-league", label: "Pro League Belgique" },
                                    { to: "/competitions/eredivisie", label: "Eredivisie Pays-Bas" },
                                ].map((sub) => (
                                    <NavLink
                                        key={sub.to}
                                        to={sub.to}
                                        className="block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F] hover:text-[#00C853] transition-all font-medium"
                                    >
                                        {sub.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 3. Cotes du Jour */}
                    <NavLink
                        to="/cotes"
                        data-testid="nav-cotes"
                        className={({ isActive }) =>
                            `text-sm font-medium transition-colors hover:text-[#00C853] ${
                                isActive ? "text-[#00C853]" : "text-slate-700 dark:text-gray-200"
                            }`
                        }
                    >
                        Cotes du Jour
                    </NavLink>

                    {/* 4. Actualités Foot */}
                    <NavLink
                        to="/"
                        data-testid="nav-actus"
                        className={({ isActive }) =>
                            `text-sm font-medium transition-colors hover:text-[#00C853] ${
                                isActive ? "text-[#00C853]" : "text-slate-700 dark:text-gray-200"
                            }`
                        }
                    >
                        Actualités Foot
                    </NavLink>

                    {/* 5. Guides & Avis Dropdown */}
                    <div
                        className="relative py-4"
                        onMouseEnter={() => setResourcesOpen(true)}
                        onMouseLeave={() => setResourcesOpen(false)}
                    >
                        <button
                            className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#00C853] ${
                                resourcesOpen ? "text-[#00C853]" : "text-slate-700 dark:text-gray-200"
                            }`}
                        >
                            Guides & Avis
                            <ChevronDown
                                size={14}
                                className={`transition-transform duration-200 ${
                                    resourcesOpen ? "rotate-180 text-[#00C853]" : ""
                                }`}
                            />
                        </button>
                        {resourcesOpen && (
                            <div className="absolute top-full left-0 mt-0.5 w-56 rounded-xl bg-card border border-border shadow-2xl p-2 animate-fade-in-up duration-200">
                                {[
                                    { to: "/guides", label: "Analyses & Guides" },
                                    { to: "/nous-contacter", label: "Nous contacter" },
                                ].map((sub) => (
                                    <NavLink
                                        key={sub.to}
                                        to={sub.to}
                                        className="block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F] hover:text-[#00C853] transition-all font-medium"
                                    >
                                        {sub.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <button
                        onClick={toggleTheme}
                        className="p-2.5 rounded-lg border border-border bg-card/40 hover:bg-card/75 text-foreground hover:text-[#00C853] transition-all flex items-center justify-center cursor-pointer"
                        aria-label="Changer de thème"
                        title={theme === "dark" ? "Passer au mode clair" : "Passer au mode sombre"}
                    >
                        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    </button>

                </div>

                <div className="flex items-center gap-2 lg:hidden">
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-lg border border-border bg-card/40 text-foreground hover:text-[#00C853] transition-all flex items-center justify-center cursor-pointer"
                        aria-label="Changer de thème"
                    >
                        {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                    <button
                        onClick={() => setOpen(!open)}
                        className="text-slate-700 dark:text-white p-2 rounded-md hover:bg-slate-100 dark:hover:bg-[#1E3A5F]"
                        aria-label="Menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl"
                    data-testid="mobile-menu"
                >
                    <div className="container-x py-4 flex flex-col gap-2">
                        {/* 1. Comparatif */}
                        <NavLink
                            to="/comparatif"
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `px-3 py-2.5 rounded-md text-sm font-medium ${
                                    isActive
                                        ? "bg-slate-100 dark:bg-[#1E3A5F] text-[#00C853]"
                                        : "text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F]"
                                }`
                            }
                        >
                            Comparatif
                        </NavLink>

                        {/* 2. Pronostics Collapsible */}
                        <div>
                            <button
                                onClick={() => setMobilePronosticsOpen(!mobilePronosticsOpen)}
                                className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F]"
                            >
                                <span>Compétitions</span>
                                <ChevronDown
                                    size={16}
                                    className={`transition-transform duration-200 ${
                                        mobilePronosticsOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {mobilePronosticsOpen && (
                                <div className="pl-6 flex flex-col gap-1 mt-1 max-h-[300px] overflow-y-auto">
                                    {[
                                        { to: "/coupe-du-monde-2026", label: "Coupe du Monde 2026" },
                                        { to: "/competitions/ligue-1", label: "Ligue 1 France" },
                                        { to: "/competitions/premier-league", label: "Premier League Angleterre" },
                                        { to: "/competitions/la-liga", label: "Liga Primera Espagne" },
                                        { to: "/competitions/serie-a", label: "Serie A Italie" },
                                        { to: "/competitions/bundesliga", label: "Bundesliga Allemagne" },
                                        { to: "/competitions/champions-league", label: "Ligue des Champions" },
                                        { to: "/competitions/europa-league", label: "Ligue Europa" },
                                        { to: "/competitions/primeira-liga", label: "Primeira Liga Portugal" },
                                        { to: "/competitions/pro-league", label: "Pro League Belgique" },
                                        { to: "/competitions/eredivisie", label: "Eredivisie Pays-Bas" },
                                    ].map((sub) => (
                                        <NavLink
                                            key={sub.to}
                                            to={sub.to}
                                            onClick={() => setOpen(false)}
                                            className="px-3 py-2 rounded-md text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-[#1E3A5F] hover:text-[#00C853]"
                                        >
                                            {sub.label}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 3. Cotes du Jour */}
                        <NavLink
                            to="/cotes"
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `px-3 py-2.5 rounded-md text-sm font-medium ${
                                    isActive
                                        ? "bg-slate-100 dark:bg-[#1E3A5F] text-[#00C853]"
                                        : "text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F]"
                                }`
                            }
                        >
                            Cotes du Jour
                        </NavLink>

                        {/* 4. Actualités Foot */}
                        <NavLink
                            to="/"
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `px-3 py-2.5 rounded-md text-sm font-medium ${
                                    isActive
                                        ? "bg-slate-100 dark:bg-[#1E3A5F] text-[#00C853]"
                                        : "text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F]"
                                }`
                            }
                        >
                            Actualités Foot
                        </NavLink>

                        {/* 5. Guides & Avis Collapsible */}
                        <div>
                            <button
                                onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
                                className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-[#1E3A5F]"
                            >
                                <span>Guides & Avis</span>
                                <ChevronDown
                                    size={16}
                                    className={`transition-transform duration-200 ${
                                        mobileResourcesOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {mobileResourcesOpen && (
                                <div className="pl-6 flex flex-col gap-1 mt-1">
                                    {[
                                        { to: "/guides", label: "Analyses & Guides" },
                                        { to: "/nous-contacter", label: "Nous contacter" },
                                    ].map((sub) => (
                                        <NavLink
                                            key={sub.to}
                                            to={sub.to}
                                            onClick={() => setOpen(false)}
                                            className="px-3 py-2 rounded-md text-sm text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-[#1E3A5F] hover:text-[#00C853]"
                                        >
                                            {sub.label}
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>


                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
